import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import type { BloxCustomDataProviderType } from 'utility/types/dataProvider';

export type RecentStoryTemplate = {
  uuid: string;
  title: string;
  type?: string;
};

export type UseRecentStoryTemplatesOptions = {
  /** Only fetch while the create menu is open to avoid needless requests. */
  enabled?: boolean;
  /** Max number of templates to return (3 x 4 grid by default). */
  limit?: number;
  /**
   * Optional text query. When provided, templates are searched server-side by
   * name so matches outside the most-recent set still surface. Callers should
   * debounce this value to avoid a request per keystroke.
   */
  searchQuery?: string;
};

export type UseRecentStoryTemplatesResult = {
  templates: RecentStoryTemplate[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
};

/**
 * Fetches the most recently created story templates across all asset types for
 * the global Create menu's "Story Templates" section. Unlike useStoryTemplates,
 * this is not scoped to a single asset type and is sorted by creation date
 * (newest first) so editors see their latest templates.
 */
export const useRecentStoryTemplates = (
  options: UseRecentStoryTemplatesOptions = {},
): UseRecentStoryTemplatesResult => {
  const { enabled = true, limit = 12, searchQuery } = options;
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const normalizedQuery = (searchQuery ?? '').trim();

  const query = useQuery<RecentStoryTemplate[], Error>({
    queryKey: ['recentStoryTemplates', limit, normalizedQuery],
    queryFn: async () => {
      const result = await dataProvider.getList('editorial', {
        pagination: { page: 1, perPage: limit },
        sort: { field: 'start_time', order: 'DESC' },
        filter: {
          template: 'yes',
          ...(normalizedQuery ? { query: normalizedQuery } : {}),
        },
      });

      const templates: RecentStoryTemplate[] = (result?.data ?? []).map((item: any) => ({
        uuid: item.uuid,
        // Title may be empty; the UI applies a translated fallback for display.
        title: item.title || '',
        type: item.type,
      }));

      return templates;
    },
    enabled,
    // Short staleTime so reopening the create menu (or changing the query)
    // picks up newly created or deleted templates without spamming requests.
    staleTime: 30 * 1000,
    placeholderData: (previous) => previous,
    retry: 1,
  });

  return {
    templates: query.data ?? [],
    // Only surface loading on the initial fetch. Background refetches (e.g. a new
    // debounced query) keep prior results visible via placeholderData.
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    refetch: query.refetch,
  };
};
